import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  EllipsisVerticalIcon,
  LockClosedIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

export function FolderActionsMenu({
  canManage = false,
  showAccess = false,
  onRename,
  onAccess,
  onDelete,
}) {
  const { t } = useTranslation();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const close = () => setOpen(false);

  const toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      close();
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 220;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const estimatedHeight = 160;
    const below = rect.bottom + 6;
    const top =
      below + estimatedHeight > window.innerHeight
        ? Math.max(8, rect.top - estimatedHeight - 6)
        : below;
    setPos({ top, left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        buttonRef.current?.contains(event.target)
      ) {
        return;
      }
      close();
    };
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  if (!canManage) return null;

  const itemClass =
    "flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-[#10221F] hover:bg-[#F5F7F6]";

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] text-[#8B9895] hover:bg-[#E6F2F0] hover:text-[#0F766E]"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("document_archive.folder_menu")}
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-[80] w-[220px] overflow-hidden rounded-[16px] border border-[#EDF0EF] bg-white py-1 shadow-[0_16px_40px_-16px_rgba(16,34,31,0.28)]"
              style={{ top: pos.top, left: pos.left }}
            >
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={(event) => {
                  event.stopPropagation();
                  close();
                  onRename?.();
                }}
              >
                <PencilSquareIcon className="h-4 w-4 text-[#0F766E]" />
                {t("document_archive.rename_folder")}
              </button>
              {showAccess ? (
                <button
                  type="button"
                  role="menuitem"
                  className={itemClass}
                  onClick={(event) => {
                    event.stopPropagation();
                    close();
                    onAccess?.();
                  }}
                >
                  <LockClosedIcon className="h-4 w-4 text-[#0F766E]" />
                  {t("document_archive.folder_access")}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className={`${itemClass} text-red-600 hover:bg-red-50`}
                onClick={(event) => {
                  event.stopPropagation();
                  close();
                  onDelete?.();
                }}
              >
                <TrashIcon className="h-4 w-4" />
                {t("document_archive.delete_folder")}
              </button>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
